import * as STD      from '@emersya/mpa-std-lib';
import * as BasketNS from './';

export class Price {

	static isSerializedPrice(something : any) : something is Price.ISerialized {
		return something &&
			typeof something === 'object' &&
			'exclTax' in something &&
			typeof something.exclTax === 'number' &&
			'inclTax' in something &&
			typeof something.inclTax === 'number' &&
			'taxes' in something &&
			typeof something.taxes === 'object' &&
			something.taxes instanceof Array &&
			something.taxes.every((x : unknown) => BasketNS.Tax.isSerializedTax(x));
	};

	static deserialize(serialized : Price.ISerialized) {
		return new this(
			new STD.math.BigNumber(serialized.exclTax),
			new STD.math.BigNumber(serialized.inclTax),
			(serialized.taxes || []).map(x => BasketNS.Tax.deserialize(x))
		);
	};

	static fromExclTax(
		exclTax : STD.math.BigNumber | number,
		taxes : BasketNS.Tax[] = []
	)
	{
		if (!(exclTax instanceof STD.math.BigNumber)) {
			exclTax = new STD.math.BigNumber(exclTax);
		}
		let inclTax = exclTax;
		for (const tax of taxes) {
			if (tax.type === 'value') {
				inclTax = inclTax.plus(tax.value);
			} else if (tax.type === 'percent') {
				inclTax = inclTax.multipliedBy(tax.value);
			}
		}
		return new this(exclTax, inclTax, taxes);
	};

	static fromInclTax(
		inclTax : STD.math.BigNumber | number,
		taxes : BasketNS.Tax[] = []
	)
	{
		if (!(inclTax instanceof STD.math.BigNumber)) {
			inclTax = new STD.math.BigNumber(inclTax);
		}
		let exclTax = inclTax;
		for (const tax of Array.from(taxes).reverse()) {
			if (tax.type === 'value') {
				exclTax = inclTax.minus(tax.value);
			} else if (tax.type === 'percent') {
				exclTax = inclTax.dividedBy(tax.value);
			}
		}
		return new this(exclTax, inclTax, taxes);
	};

	static ofZero(taxes : BasketNS.Tax[] = []) {
		return (new this(
			new STD.math.BigNumber(0),
			new STD.math.BigNumber(0),
			taxes
		));
	};

	exclTax : STD.math.BigNumber;
	inclTax : STD.math.BigNumber;
	readonly taxes : BasketNS.Tax[];

	isNull() {
		return (this.inclTax.toNumber() === 0) &&
			(this.exclTax.toNumber() === 0)
			;
	};

	clone() {
		return (new Price(this.exclTax, this.inclTax, this.taxes));
	};

	add(otherPrice : Price) {
		this.exclTax = this.exclTax.plus(otherPrice.exclTax);
		this.inclTax = this.inclTax.plus(otherPrice.inclTax);
	};

	cropTo2Decimal() {
		this.exclTax = this.exclTax.decimalPlaces(2);
		this.inclTax = this.inclTax.decimalPlaces(2);
	};

	serialize() : Price.ISerialized {
		return {
			exclTax : this.exclTax.toNumber(),
			inclTax : this.inclTax.toNumber(),
			taxes :   this.taxes.map(x => x.serialize())
		};
	};

	constructor(
		exclTax : STD.math.BigNumber,
		inclTax : STD.math.BigNumber,
		taxes : BasketNS.Tax[]
	)
	{
		this.exclTax = exclTax;
		this.inclTax = inclTax;
		this.taxes = taxes;
	};
};
export namespace Price {
	export interface ISerialized {
		readonly exclTax : number;
		readonly inclTax : number;
		readonly taxes : BasketNS.Tax.ISerialized[];
	};

	export class FinalPrice extends Price {
		declare readonly exclTax : STD.math.BigNumber;
		declare readonly inclTax : STD.math.BigNumber;

		constructor(...parameters : ConstructorParameters<typeof Price>) {
			super(...parameters);
			Object.freeze(this.exclTax);
			Object.freeze(this.inclTax);
			Object.freeze(this.taxes);
		};
	};
}
